import React, { useState } from 'react';
import { Wrench, Phone, Mail, MapPin, BriefcaseIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function PartnerForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    experience: '',
    expertise: [] as string[],
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const expertiseOptions = [
    'AC Repair',
    'TV Repair',
    'Washing Machine Repair',
    'Refrigerator Repair',
    'RO/Water Purifier',
    'Microwave Repair'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('partner_applications')
        .insert([formData]);

      if (error) throw error;
      
      setSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        experience: '',
        expertise: [],
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Wrench className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Application Submitted!</h3>
        <p className="text-gray-600">
          Thank you for your interest in partnering with us. Our team will review your application and contact you soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Phone className="inline-block w-4 h-4 mr-2" />
          Full Name
        </label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your full name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Phone className="inline-block w-4 h-4 mr-2" />
          Phone Number
        </label>
        <input
          type="tel"
          required
          pattern="[0-9]{10}"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter 10-digit mobile number"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Mail className="inline-block w-4 h-4 mr-2" />
          Email (Optional)
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your email address"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="inline-block w-4 h-4 mr-2" />
          City
        </label>
        <input
          type="text"
          required
          value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your city"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <BriefcaseIcon className="inline-block w-4 h-4 mr-2" />
          Years of Experience
        </label>
        <select
          required
          value={formData.experience}
          onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select experience</option>
          <option value="0-2">0-2 years</option>
          <option value="2-5">2-5 years</option>
          <option value="5-10">5-10 years</option>
          <option value="10+">10+ years</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Wrench className="inline-block w-4 h-4 mr-2" />
          Areas of Expertise
        </label>
        <div className="grid grid-cols-2 gap-2">
          {expertiseOptions.map((option) => (
            <label key={option} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.expertise.includes(option)}
                onChange={(e) => {
                  const newExpertise = e.target.checked
                    ? [...formData.expertise, option]
                    : formData.expertise.filter(item => item !== option);
                  setFormData({ ...formData, expertise: newExpertise });
                }}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Additional Information
        </label>
        <textarea
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Tell us about your work experience and certifications..."
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 
          disabled:opacity-50 transition-colors"
      >
        {loading ? 'Submitting...' : 'Submit Application'}
      </button>
    </form>
  );
}